import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, 
    IconButton, Tooltip, TextField, Chip, Divider, Stack 
} from '@mui/material';
import { 
    Close as CloseIcon, 
    Casino as CasinoIcon, 
    CardGiftcard as BlindBagIcon, 
    Settings as SettingsIcon,
    Shuffle as ShuffleIcon,
    PlaylistAddCheck as PresetIcon,
    Check as CheckIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import { Wheel } from 'react-custom-roulette';
import BlindBagCalling from './BlindBagCalling';
import { v4 as uuidv4 } from 'uuid';

export interface CallingStudent {
    student_id: string;
    student_name: string;
}

export interface LuckyWheelModalProps {
    open: boolean;
    onClose: () => void;
    students?: CallingStudent[];
    initialMode?: 'wheel' | 'blindbag';
    allowCustomList?: boolean;
    onStudentsChange?: (newStudents: CallingStudent[]) => void;
}

export const CALLING_PRESETS: { name: string; icon: string; items: string[] }[] = [
    {
        name: 'Học sinh mẫu (10 bạn)',
        icon: '🎓',
        items: [
            'Nguyễn Văn An',
            'Trần Thị Bình',
            'Lê Hoàng Cường',
            'Phạm Minh Dũng',
            'Vũ Hương Giang',
            'Đặng Thuỳ Linh',
            'Hoàng Minh Nam',
            'Bùi Lan Phương',
            'Đỗ Tuấn Quân',
            'Ngô Thuỳ Trang'
        ]
    },
    {
        name: 'Số thứ tự (1 - 20)',
        icon: '🔢',
        items: Array.from({ length: 20 }, (_, i) => `Số ${i + 1}`)
    },
    {
        name: 'Phần thưởng may mắn',
        icon: '🎁',
        items: [
            '10 điểm thưởng',
            'Tràng pháo tay giòn giã',
            'Sticker siêu cute',
            'Bút chì may mắn',
            'Cộng 2 điểm kiểm tra',
            'Một món quà bí mật',
            'Miễn 1 bài tập về nhà',
            'Được chọn chỗ ngồi'
        ]
    },
    {
        name: 'Thử thách vui nhộn',
        icon: '🎯',
        items: [
            'Hát 1 bài hát ngắn',
            'Kể 1 câu chuyện vui',
            'Nhảy 1 điệu vui nhộn 15s',
            'Đọc 1 bài thơ 4 câu',
            'Làm 1 động tác dễ thương',
            'Khen 1 người bạn bên cạnh',
            'Giải 1 câu đố mẹo'
        ]
    }
];

const LOCAL_STORAGE_KEY = 'global_calling_items';

export const getStoredCallingItems = (): CallingStudent[] => {
    try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed;
            }
        }
    } catch (e) {
        console.warn('Error reading stored calling items', e);
    }
    return CALLING_PRESETS[0].items.map(name => ({
        student_id: uuidv4(),
        student_name: name
    }));
};

const LuckyWheelModal: React.FC<LuckyWheelModalProps> = ({ 
    open, 
    onClose, 
    students, 
    initialMode = 'wheel',
    allowCustomList = true,
    onStudentsChange
}) => {
    const [mode, setMode] = useState<'wheel' | 'blindbag'>(initialMode);
    const [activeStudents, setActiveStudents] = useState<CallingStudent[]>(() => {
        if (students && students.length > 0) return students;
        return getStoredCallingItems();
    });
    const [mustSpin, setMustSpin] = useState(false);
    const [prizeNumber, setPrizeNumber] = useState(0);
    const [winner, setWinner] = useState<string | null>(null);

    // Config dialog state
    const [configOpen, setConfigOpen] = useState(false);
    const [configText, setConfigText] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (open && initialMode) {
            setMode(initialMode);
        }
        if (!open) {
            setIsFullscreen(false);
        }
    }, [open, initialMode]);

    useEffect(() => {
        if (students && students.length > 0) {
            setActiveStudents(students);
        }
    }, [students]);

    const handleOpenConfig = () => {
        const text = activeStudents.map(s => s.student_name).join('\n');
        setConfigText(text);
        setConfigOpen(true);
    };

    const handleApplyConfig = () => {
        const lines = configText
            .split('\n')
            .map(l => l.trim())
            .filter(l => l.length > 0);

        const newItems: CallingStudent[] = lines.length > 0 
            ? lines.map(name => ({ student_id: uuidv4(), student_name: name }))
            : CALLING_PRESETS[0].items.map(name => ({ student_id: uuidv4(), student_name: name }));

        setActiveStudents(newItems);
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newItems));
        } catch (e) {
            console.warn('Error saving calling items', e);
        }
        if (onStudentsChange) {
            onStudentsChange(newItems);
        }
        setWinner(null);
        setConfigOpen(false);
    };

    const handleSelectPreset = (preset: typeof CALLING_PRESETS[0]) => {
        setConfigText(preset.items.join('\n'));
    };

    const handleShuffleConfig = () => {
        const lines = configText
            .split('\n')
            .map(l => l.trim())
            .filter(l => l.length > 0);
        
        for (let i = lines.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [lines[i], lines[j]] = [lines[j], lines[i]];
        }
        setConfigText(lines.join('\n'));
    };

    const handleSpinClick = () => {
        if (!mustSpin && activeStudents.length > 0) {
            const newPrizeNumber = Math.floor(Math.random() * activeStudents.length);
            setPrizeNumber(newPrizeNumber);
            setMustSpin(true);
            setWinner(null);
        }
    };

    const handleStopSpinning = () => {
        setMustSpin(false);
        const winningStudent = activeStudents[prizeNumber]?.student_name || '';
        setWinner(winningStudent);
        
        // Play TTS
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
        const url = `${baseURL}/utils/tts?text=${encodeURIComponent('Đã chọn ' + winningStudent)}&lang=vi`;
        const audio = new Audio(url);
        audio.play().catch(e => console.error('TTS play error:', e));
    };

    // Modern vibrant color palette
    const backgroundColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FDCB6E', '#6C5CE7', '#FD79A8', '#00B894', '#0984E3'];

    // Map students to wheel format, limit name length or use last name
    const wheelData = activeStudents.length > 0 ? activeStudents.map((s) => {
        const nameParts = s.student_name.split(' ');
        let displayName = nameParts[nameParts.length - 1]; // Use last name by default
        if (nameParts.length > 1) {
             displayName = nameParts[nameParts.length - 2] + ' ' + displayName;
        }
        return { option: displayName };
    }) : [{ option: 'Trống' }];

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth={isFullscreen ? false : (mode === 'blindbag' ? 'md' : 'sm')} 
            fullWidth 
            fullScreen={isFullscreen}
            PaperProps={{ 
                sx: { 
                    background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
                    borderRadius: isFullscreen ? 0 : 3,
                    overflow: 'hidden',
                    maxWidth: isFullscreen ? '100vw' : (mode === 'blindbag' ? 680 : 500),
                    width: '100%',
                    maxHeight: isFullscreen ? '100vh' : '94vh',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                } 
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5, pt: 1.2, px: 2, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', bgcolor: '#e2e8f0', p: 0.4, borderRadius: 3 }}>
                        <Button
                            size="small"
                            onClick={() => setMode('wheel')}
                            disabled={mustSpin}
                            startIcon={<CasinoIcon fontSize="small" />}
                            sx={{
                                borderRadius: 2.5,
                                px: 1.5,
                                py: 0.4,
                                fontSize: '0.82rem',
                                fontWeight: 700,
                                textTransform: 'none',
                                bgcolor: mode === 'wheel' ? '#fff' : 'transparent',
                                color: mode === 'wheel' ? 'primary.main' : '#64748b',
                                boxShadow: mode === 'wheel' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                                '&:hover': { bgcolor: mode === 'wheel' ? '#fff' : 'rgba(0,0,0,0.04)' }
                            }}
                        >
                            Vòng quay
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setMode('blindbag')}
                            disabled={mustSpin}
                            startIcon={<BlindBagIcon fontSize="small" sx={{ color: mode === 'blindbag' ? '#ec4899' : 'inherit' }} />}
                            sx={{
                                borderRadius: 2.5,
                                px: 1.5,
                                py: 0.4,
                                fontSize: '0.82rem',
                                fontWeight: 700,
                                textTransform: 'none',
                                bgcolor: mode === 'blindbag' ? '#fff' : 'transparent',
                                color: mode === 'blindbag' ? '#be185d' : '#64748b',
                                boxShadow: mode === 'blindbag' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
                                '&:hover': { bgcolor: mode === 'blindbag' ? '#fff' : 'rgba(0,0,0,0.04)' }
                            }}
                        >
                            Xé túi mù ✨
                        </Button>
                    </Box>

                    {allowCustomList && (
                        <Tooltip title="Cấu hình danh sách tên / mục quay">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleOpenConfig}
                                disabled={mustSpin}
                                startIcon={<SettingsIcon sx={{ fontSize: 15 }} />}
                                sx={{
                                    borderRadius: 2.5,
                                    px: 1.2,
                                    py: 0.4,
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    borderColor: '#cbd5e1',
                                    color: '#475569',
                                    bgcolor: '#fff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                                }}
                            >
                                Danh sách ({activeStudents.length})
                            </Button>
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={isFullscreen ? "Thu nhỏ cửa sổ" : "Toàn màn hình trình chiếu (Bục giảng)"}>
                        <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small" sx={{ color: '#475569' }}>
                            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Tooltip>
                    <IconButton onClick={onClose} size="small" disabled={mustSpin}><CloseIcon /></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                py: 1, 
                px: 2, 
                overflow: 'hidden !important',
                overflowY: 'hidden !important',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}>
                {activeStudents.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>Chưa có mục nào trong danh sách.</Typography>
                        <Button variant="contained" size="small" onClick={handleOpenConfig} startIcon={<SettingsIcon />}>
                            Cấu hình danh sách ngay
                        </Button>
                    </Box>
                ) : mode === 'blindbag' ? (
                    <BlindBagCalling students={activeStudents} isFullscreen={isFullscreen} />
                ) : (
                    <Box sx={{ 
                        position: 'relative', 
                        width: '100%', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        height: { xs: 310, sm: 385 },
                        overflow: 'hidden'
                    }}>
                        <Box sx={{ 
                            transform: { xs: 'scale(0.7)', sm: 'scale(0.85)' }, 
                            transformOrigin: 'center center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <Wheel
                                mustStartSpinning={mustSpin}
                                prizeNumber={prizeNumber}
                                data={wheelData}
                                backgroundColors={backgroundColors}
                                textColors={['#ffffff']}
                                onStopSpinning={handleStopSpinning}
                                spinDuration={0.8}
                                outerBorderColor="#E2E8F0"
                                outerBorderWidth={5}
                                innerBorderColor="#ffffff"
                                innerBorderWidth={0}
                                innerRadius={10}
                                radiusLineColor="#ffffff"
                                radiusLineWidth={1}
                                fontSize={15}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>
            {mode === 'wheel' && (
                <DialogActions sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pb: 3, pt: 1, px: 3, gap: 1.5 }}>
                    {activeStudents.length > 0 && (
                        <Box sx={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            {winner ? (
                                <Box sx={{ 
                                    bgcolor: '#fff', 
                                    px: 3, 
                                    py: 1, 
                                    borderRadius: 3, 
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    border: '2px solid #FFD700'
                                }}>
                                    <Typography variant="h6" color="#d32f2f" fontWeight="900" textAlign="center" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        🎉 {winner} 🎉
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="body1" color="text.secondary" sx={{ opacity: 0.8, fontWeight: 'medium' }}>
                                    Bấm QUAY để gọi ngẫu nhiên
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="large"
                        onClick={handleSpinClick}
                        disabled={mustSpin || activeStudents.length === 0}
                        sx={{ width: '100%', maxWidth: 280, fontWeight: 'bold', fontSize: '1.1rem', borderRadius: 8, height: 46, boxShadow: 3, m: '0 !important' }}
                    >
                        {mustSpin ? 'ĐANG QUAY...' : 'QUAY!'}
                    </Button>
                </DialogActions>
            )}

            {/* List Configuration Sub-Dialog */}
            <Dialog
                open={configOpen}
                onClose={() => setConfigOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PresetIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">Cấu hình danh sách gọi / mục quay</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setConfigOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Chọn bộ mẫu có sẵn hoặc nhập danh sách tùy ý (mỗi dòng một tên/mục):
                    </Typography>

                    {/* Preset Chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {CALLING_PRESETS.map((preset) => (
                            <Chip
                                key={preset.name}
                                icon={<span>{preset.icon}</span>}
                                label={preset.name}
                                onClick={() => handleSelectPreset(preset)}
                                variant="outlined"
                                clickable
                                sx={{ 
                                    borderRadius: 2, 
                                    fontWeight: 600,
                                    '&:hover': { bgcolor: '#eff6ff', borderColor: '#3b82f6' }
                                }}
                            />
                        ))}
                    </Box>

                    <TextField
                        multiline
                        rows={8}
                        fullWidth
                        variant="outlined"
                        placeholder="Nhập hoặc dán tên, mỗi dòng một mục..."
                        value={configText}
                        onChange={(e) => setConfigText(e.target.value)}
                        helperText={`Đang có ${configText.split('\n').filter(l => l.trim().length > 0).length} mục trong danh sách`}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                bgcolor: '#f8fafc',
                                borderRadius: 2
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button 
                        startIcon={<ShuffleIcon />} 
                        onClick={handleShuffleConfig} 
                        color="secondary"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Xáo trộn
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={() => setConfigOpen(false)} sx={{ textTransform: 'none' }}>
                            Hủy
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<CheckIcon />} 
                            onClick={handleApplyConfig}
                            sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, px: 2.5 }}
                        >
                            Áp dụng & Lưu
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default LuckyWheelModal;
